import { useTranslate } from '@tolgee/react';
import { Dropdown, Menu } from 'antd';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { getTiktokProxyImageSrc } from 'lib/utils/platformImage';
import { useMemo, VideoHTMLAttributes, useState, useEffect, useRef } from 'react';
import copyText from 'utils/copyText';
import { shopeeVideoBackupService } from 'services/domainBackup';
import { shopeeVideoDomains } from 'utils/constants';

/**
 * 判断 URL 是否是 Shopee 视频
 */
const isShopeeVideo = (url: string): boolean => {
  if (!url) {
    return false;
  }
  try {
    const urlObj = new URL(url);
    return shopeeVideoDomains.some((domain) => urlObj.hostname === domain);
  } catch {
    // 如果 URL 解析失败，尝试简单的字符串匹配
    return shopeeVideoDomains.some((domain) => url.includes(domain));
  }
};

function ProxyVideo({ src: srcProp, ...otherProps }: VideoHTMLAttributes<HTMLVideoElement>) {
  const { t } = useTranslate();

  const { tiktokImageProxyOriginList } = useOrgConfigStateValue();

  // Shopee 视频备份 URL 状态
  const [backupUrl, setBackupUrl] = useState<string | null>(null);
  // 是否正在尝试备份 URL
  const isTryingBackupRef = useRef(false);
  // 是否已经开始播放
  const hasStartedPlayingRef = useRef(false);

  // 判断是否是 Shopee 视频
  const isShopee = useMemo(() => {
    return srcProp ? isShopeeVideo(srcProp) : false;
  }, [srcProp]);

  // 初始加载时，如果是 Shopee 视频，尝试获取备份 URL
  useEffect(() => {
    if (isShopee && srcProp && !isTryingBackupRef.current) {
      isTryingBackupRef.current = true;
      shopeeVideoBackupService
        .findFastestBackupDomain(srcProp)
        .then((newUrl) => {
          if (newUrl && newUrl !== srcProp) {
            setBackupUrl(newUrl);
          }
        })
        .finally(() => {
          isTryingBackupRef.current = false;
        });
    }
  }, [isShopee, srcProp]);

  // 当 srcProp 变化时，重置状态
  useEffect(() => {
    if (srcProp) {
      setBackupUrl(null);
      hasStartedPlayingRef.current = false;
      isTryingBackupRef.current = false;
    }
  }, [srcProp]);

  const src = useMemo(() => {
    if (!srcProp) {
      return srcProp;
    }

    // 优先使用备份 URL（如果存在）
    if (isShopee && backupUrl) {
      return backupUrl;
    }

    if (tiktokImageProxyOriginList?.some((origin) => srcProp.includes(origin))) {
      return getTiktokProxyImageSrc(srcProp);
    }

    return srcProp;
  }, [srcProp, tiktokImageProxyOriginList, isShopee, backupUrl]);

  // 处理视频加载错误
  const handleError = async (event: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    if (!srcProp || !isShopee) {
      if (otherProps.onError) {
        otherProps.onError(event);
      }
      return;
    }

    // 如果当前使用的是备份 URL，标记该域名为不可用
    if (backupUrl) {
      shopeeVideoBackupService.markDomainAsUnavailable(backupUrl);
      // 尝试获取新的备份 URL
      const newBackupUrl = await shopeeVideoBackupService.findFastestBackupDomain(srcProp);
      if (newBackupUrl && newBackupUrl !== backupUrl) {
        setBackupUrl(newBackupUrl);
      } else {
        // 如果所有备份都失败，回退到原始 URL
        setBackupUrl(null);
      }
    } else {
      // 如果当前使用的是原始 URL，标记为不可用并尝试备份
      shopeeVideoBackupService.markDomainAsUnavailable(srcProp);
      const newBackupUrl = await shopeeVideoBackupService.findFastestBackupDomain(srcProp);
      if (newBackupUrl && newBackupUrl !== srcProp) {
        setBackupUrl(newBackupUrl);
      }
    }

    // 调用原始的 onError 处理器
    if (otherProps.onError) {
      otherProps.onError(event);
    }
  };

  // 处理视频开始播放
  const handlePlaying = (event: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    hasStartedPlayingRef.current = true;
    if (otherProps.onPlaying) {
      otherProps.onPlaying(event);
    }
  };

  const video = <video src={src} {...otherProps} onError={handleError} onPlaying={handlePlaying} />;

  if (!srcProp) {
    return video;
  }

  return (
    <Dropdown
      trigger={['contextMenu']}
      overlay={
        <Menu
          onClick={({ domEvent }) => {
            domEvent.stopPropagation();
            copyText(srcProp);
          }}
        >
          <Menu.Item>{t('authShop.复制链接')}</Menu.Item>
        </Menu>
      }
    >
      {video}
    </Dropdown>
  );
}

export default ProxyVideo;
